import { useFlags } from "launchdarkly-react-client-sdk";
import { MotionConfig } from "motion/react";
import { classes } from "lib/actions/util";
import { routesDefinitions } from "lib/actions/routeDefinitions";
import { useSideNav } from "lib/contexts/ProjectContexts/SideNavContext";
import { modelPageTypes } from "lib/types/assets/ModelPageType";
import KeyframeLogoFullSvg from "components/icons/keyframe-logo-full.svg";
import KeyframeLogoSvg from "components/icons/keyframe-logo.svg";
import ExpansionSvg from "components/icons/24/main-menu/menu-expand.svg";
import LayersSvg from "components/icons/24/main-menu/menu-layers.svg";
import ViewsSvg from "components/icons/24/main-menu/menu-views.svg";
import SearchSvg from "components/icons/24/tools/tool-search.svg";
import BimSvg from "components/icons/24/AssetTypeIcons/asset_type-model.svg";
import PointCloudSvg from "components/icons/24/AssetTypeIcons/asset_type-pointcloud.svg";
import GisSvg from "components/icons/24/AssetTypeIcons/asset_type-gis.svg";
import PanoramicImagerySvg from "components/icons/24/AssetTypeIcons/asset_type-scanset.svg";
import IssuesSvg from "components/icons/24/main-menu/menu-issues.svg";
import PeopleSvg from "components/icons/24/main-menu/menu-contacts.svg";
import SettingsSvg from "components/icons/24/main-menu/menu-settings.svg";
import SideNavItem from "./SideNavItem";
import SideNavItemIcon from "./SideNavItemIcon";
import SideNavSectionItem from "./SideNavSectionItem";
import SideNavDivider from "./SideNavDivider";

export default function SideNav(p: { className?: string }) {
  const { routes, isExpanded, toggleExpanded } = useSideNav();
  const {
    moduleIssueTracking: hasIssueTracking,
    featureLayers: hasLayers,
    featureScansets: hasScansets,
  } = useFlags();

  return (
    <MotionConfig reducedMotion="user">
      <nav
        className={classes(
          "flex h-full flex-col justify-between rounded-xl bg-white shadow-1 transition-all duration-300 ease-in-out",
          isExpanded ? "w-64" : "w-16",
          p.className,
        )}
      >
        <div className="flex flex-col py-2">
          {/* mb-2.5 lines the first item's tile up with the top of the side panel, below the header */}
          <div className="mb-2.5 flex h-12 items-center overflow-hidden">
            {isExpanded ? (
              <div className="ml-5 w-full ">
                <KeyframeLogoFullSvg className="mt-1 h-6" />
              </div>
            ) : (
              <div className="w-full justify-items-center">
                <KeyframeLogoSvg className="h-6" />
              </div>
            )}
          </div>
          {/* A tab per model type, each taking its asset type colour when selected */}
          <SideNavSectionItem
            svg={BimSvg}
            label={modelPageTypes.models.label}
            href={routes.models}
            activeRegex={routesDefinitions.models.regex}
            group="models"
            activeColorClassName="text-indigo-500"
          />
          <SideNavSectionItem
            svg={PointCloudSvg}
            label={modelPageTypes.scans.label}
            href={routes.scans}
            activeRegex={routesDefinitions.scans.regex}
            group="models"
            activeColorClassName="text-purple-500"
          />
          <SideNavSectionItem
            svg={GisSvg}
            label={modelPageTypes.gis.label}
            href={routes.gis}
            activeRegex={routesDefinitions.gis.regex}
            group="models"
            activeColorClassName="text-green-500"
          />
          {hasScansets && (
            <SideNavSectionItem
              svg={PanoramicImagerySvg}
              label={modelPageTypes.imagery.label}
              href={routes.imagery}
              activeRegex={routesDefinitions.imagery.regex}
              group="models"
              activeColorClassName="text-sky-500"
            />
          )}
          <SideNavDivider />

          {hasLayers && (
            <>
              <SideNavSectionItem
                svg={LayersSvg}
                label="Layers"
                href={routes.layers}
                activeRegex={routesDefinitions.layers.regex}
                group="layers"
                activeColorClassName="text-indigo-500"
              />
              <SideNavDivider />
            </>
          )}

          <SideNavSectionItem
            svg={ViewsSvg}
            label="Views"
            href={routes.views}
            activeRegex={routesDefinitions.views.regex}
            group="personal"
          />
          <SideNavSectionItem
            svg={SearchSvg}
            label="Search"
            href={routes.search}
            activeRegex={routesDefinitions.search.regex}
            group="personal"
          />

          {hasIssueTracking && (
            <>
              <SideNavDivider />
              <SideNavSectionItem
                svg={IssuesSvg}
                label="Issues"
                href={routes.issues}
                activeRegex={routesDefinitions.issues.regex}
                group="project"
                activeColorClassName="text-red-500"
              />
            </>
          )}
        </div>

        <div className="flex flex-col py-2">
          <SideNavSectionItem
            svg={PeopleSvg}
            label="Members"
            href={routes.members}
            activeRegex={routesDefinitions.members.regex}
            group="admin"
          />
          <SideNavSectionItem
            svg={SettingsSvg}
            label="Settings"
            href={routes.projectSettings}
            activeRegex={routesDefinitions.projectSettings.regex}
            group="admin"
          />
          <SideNavItem
            icon={<SideNavItemIcon svg={ExpansionSvg} />}
            label={!isExpanded ? "Expand menu" : "Collapse menu"}
            onClick={() => toggleExpanded()}
          />
        </div>
      </nav>
    </MotionConfig>
  );
}
